/* App.js */
import { useContext, useEffect, useState, useRef } from "react";
import {
  Switch,
  Route,
  Redirect,
  useHistory,
} from "react-router-dom";

/* 결제 페이지 개폐 스위치 (2026-09-08 김현비).
   결제·주문은 네이버 스마트스토어에서 하기로 해서 쿠키스토리 결제 페이지는 닫아 뒀다.
   PaymentPage 코드·PortOne 연동은 그대로 남아 있으니, 다시 받으려면 이 값만 true 로 바꾸면 된다. */
const PAYMENT_OPEN = false;

import { PRICING_OPEN } from "./config/features";
import HomePage from "./pages/Home";
import DesignPage from "./pages/Design";
import CutterPage from "./pages/Cutter/Cutter.js";
import GalleryPage from "./pages/Gallery";

import { useTranslation } from "react-i18next";
import i18n from "./i18n";

import { supabase } from "./supabase";
import { AuthContext } from "./context/AuthContext";
import { AuthProvider } from "./context/AuthContext";

import LoginPage from "./pages/Auth/Login";
import RegisterPage from "./pages/Auth/Register";
import MyPage from "./pages/MyPage/MyPage";
import ForgotPasswordPage from "./pages/Auth/ForgotPassword";
import UpdatePasswordPage from "./pages/Auth/UpdatePassword";

import { ToastProvider } from "./components/Toast";
import PricingPage from "./pages/Pricing/PricingPage";
import PaymentSuccess from "./pages/Payment/PaymentSuccess";
import PaymentCancel from "./pages/Payment/PaymentCancel";
import TossBillingSuccess from "./pages/Payment/TossBillingSuccess";
import TossBillingFail from "./pages/Payment/TossBillingFail";
import TossPaymentSuccess from "./pages/Payment/TossPaymentSuccess";
import TossPaymentFail from "./pages/Payment/TossPaymentFail";
import KakaoPaymentSuccess from "./pages/Payment/KakaoPaymentSuccess";
import KakaoPaymentFail from "./pages/Payment/KakaoPaymentFail";
import KakaoBillingSuccess from "./pages/Payment/KakaoBillingSuccess";
import KakaoBillingFail from "./pages/Payment/KakaoBillingFail";

import GalleryWrite from "./pages/Gallery/GalleryWrite";
import GalleryDetail from "./pages/Gallery/GalleryDetail";

import Footer from "./components/Footer";
import PrivacyPage from "./pages/Policy/PrivacyPage";
import TermsPage from "./pages/Policy/TermsPage";
import ServiceInfoPage from "./pages/Policy/ServiceInfoPage";
import UserGuidePage from "./pages/Guide/UserGuidePage";
import OrderPage from "./pages/Order/OrderPage";
import PaymentPage from "./pages/Order/PaymentPage";
import OrderLookup from "./pages/Order/OrderLookup";
import EmbedOrderPage from "./pages/Order/EmbedOrderPage";
// 새 자동 견적 화면 미리보기 — 전환(B단계 10) 때 이 줄과 아래 라우트를 지운다
import useQuoteRegion from "./hooks/useQuoteRegion";
import PrintingFAQ from "./pages/PrintingFAQ";
import useHappytalk from "./hooks/useHappytalk";
import { closeHappytalk } from "./utils/happytalk";

/* ===== 관리자 페이지 ===== */
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";

import { useLocation } from "react-router-dom";
import { rememberStoreFromSearch } from "./utils/storeOrigin";
import { trackPageView } from "./utils/gtag";
import { setAdTrackingAllowed, trackAdPage } from "./utils/adAnalytics";

function AppContent() {
  const { user, loading } = useContext(AuthContext);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const openContact = useHappytalk();
  const langRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  useEffect(() => { closeHappytalk(); }, [location.pathname]);
  const { t } = useTranslation();

  // ?store=makerz|kkuk — 스토어별 블로그 글에서 들어온 손님의 결제 스토어를 기억 (2026-10-21)
  useEffect(() => { rememberStoreFromSearch(location.search); }, [location.search]);

  const lastAdPage = useRef(null);
  useEffect(() => {
    const allowed = !loading && user?.role !== "admin" && !location.pathname.startsWith("/admin");
    setAdTrackingAllowed(allowed);
    if (!allowed) { lastAdPage.current = null; return; }
    if (lastAdPage.current !== location.pathname) {
      lastAdPage.current = location.pathname;
      trackAdPage(location.pathname);
    }
  }, [loading, user?.role, location.pathname]);

  const isAdminRoute = window.location.hash.startsWith("/admin");
  const isEmbedRoute = location.pathname.startsWith("/embed");
  // 상단 메뉴에 지금 보고 있는 페이지 표시 (2026-10-16 김현비: 진한 글자 + 캐러멜 밑줄)
  const navOn = (...paths) =>
    paths.some((p) => (p === "/" ? location.pathname === "/" : location.pathname === p || location.pathname.startsWith(p + "/"))) ? "on" : "";
  // 해외배송을 하지 않으므로 주문 메뉴는 국내 접속에만 보인다 (설계 4).
  // null = 아직 확인 중 — 메뉴가 나타났다 사라지지 않게 그동안은 감춰 둔다.
  const orderMenuVisible = useQuoteRegion() === true;

  // 페이지 이동 시 스크롤 맨 위로
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // 구글 태그 page_view — SPA 라 화면 이동이 JS 로 일어난다.
  // index.html 에서 자동 page_view 를 꺼 뒀으므로 여기서 화면당 1회 보낸다.
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pageViewRef = useRef({ id: null, startTime: null });

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    if (user?.role === "admin") return;

    // 이전 페이지 체류시간 업데이트
    const prev = pageViewRef.current;
    if (prev.id && prev.startTime) {
      const duration = Math.round((Date.now() - prev.startTime) / 1000);
      if (duration > 0 && duration < 3600) {
        supabase
          .from("page_views")
          .update({ duration_sec: duration })
          .eq("id", prev.id)
          .then();
      }
    }

    // 새 페이지뷰 기록
    supabase
      .from("page_views")
      .insert({
        path: location.pathname,
        user_id: user?.id ?? null,
      })
      .select("id")
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error("page_view insert failed:", error.message);
          return;
        }
        pageViewRef.current = {
          id: data?.id ?? null,
          startTime: Date.now(),
        };
      });

    return () => {
      // 페이지 떠날 때 체류시간 업데이트 (언마운트)
      const cur = pageViewRef.current;
      if (cur.id && cur.startTime) {
        const duration = Math.round((Date.now() - cur.startTime) / 1000);
        if (duration > 0 && duration < 3600) {
          supabase
            .from("page_views")
            .update({ duration_sec: duration })
            .eq("id", cur.id)
            .then();
        }
      }
    };
  }, [location.pathname]);

  if (loading) return null;

  function go(path) {
    history.push(path);
    setMobileMenuOpen(false);
  }

  async function logout() {
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  return (
    <div className="app-wrapper">
      {!isAdminRoute && !isEmbedRoute && (
        <>
        <div className="cc-header">
          <div className="header-left" onClick={() => go("/")}>
            <img src="/favicon.svg" className="brand-logo" width={25} />
            <span className="brand-text">CookiStory</span>
          </div>

          <div className="header-nav">
            <button className={navOn("/")} onClick={() => go("/")}>{t("nav.home")}</button>
            {/* 2026-10-15 리뉴얼: 쿠키커터 만들기가 대표 기능 → AI 도안보다 앞 */}
            <button className={navOn("/cutter")} onClick={() => go("/cutter")}>{t("nav.cutter")}</button>
            <button className={navOn("/design")} onClick={() => go("/design")}>
              {t("nav.design")}
            </button>
            <button className={navOn("/gallery")} onClick={() => go("/gallery")}>
              {t("nav.gallery")}
            </button>
            {orderMenuVisible && <button className={navOn("/order")} onClick={() => go("/order")}>{t("nav.order")}</button>}
            <div className="nav-dropdown">
              <button className={`nav-dropdown-trigger ${navOn("/guide", "/printing-faq")}`} onClick={() => go("/guide")}>{t("nav.customerSupport")}</button>
              <div className="nav-dropdown-menu">
                {/* 2026-10-16 고객지원 개편: 3D프린팅·쿠키커터 안내를 FAQ 한 장으로 합침 */}
                <button onClick={() => go("/guide")}>{t("nav.guide")}</button>
                <button onClick={() => go("/printing-faq")}>{t("nav.faq")}</button>
                <hr style={{ margin: "4px 0", border: "none", borderTop: "1px solid #f0ece8" }} />
                <button onClick={openContact}>{t("nav.contact")}</button>
              </div>
            </div>
            {PRICING_OPEN && (
              <button onClick={() => go("/pricing")}>
                {t("nav.pricing")}
              </button>
            )}
          </div>

          <div className="header-right">
            {user?.role === "admin" && (
              <button onClick={() => go("/admin")} className="admin-nav-btn">
                Admin
              </button>
            )}
            <div className="lang-wrapper" ref={langRef}>
              <button
                className="lang-btn"
                onClick={() => setLangOpen(!langOpen)}
              >
                🌐
              </button>

              {langOpen && (
                <div className="lang-dropdown">
                  <div onClick={() => { i18n.changeLanguage("en"); setLangOpen(false); }}>English</div>
                  <div onClick={() => { i18n.changeLanguage("ko"); setLangOpen(false); }}>한국어</div>
                  <div onClick={() => { i18n.changeLanguage("zh"); setLangOpen(false); }}>简体中文</div>
                  <div onClick={() => { i18n.changeLanguage("ja"); setLangOpen(false); }}>日本語</div>
                  <div onClick={() => { i18n.changeLanguage("es"); setLangOpen(false); }}>Español</div>
                </div>
              )}
            </div>

            <div className="login-section">
              {user ? (
                <>
                  <span className="user-email">{user.email}</span>

                  <button className="login-btn" onClick={() => go("/mypage")}>
                    {t("nav.mypage")}
                  </button>

                  <button className="logout-btn" onClick={logout}>
                    {t("auth.logout")}
                  </button>
                </>
              ) : (
                <>
                  {/* 비회원 주문조회 메뉴 제거 (2026-10-16 김현비) — 주문·결제가 네이버 스마트스토어로 옮겨가 쓸 일이 없다. 옛 링크용 /order/lookup 경로는 남김 */}
                  <button className="login-btn" onClick={() => go("/login")}>
                    {t("auth.login")}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 모바일 햄버거 버튼 */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>
        </div>

        {/* 모바일 메뉴 */}
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
            <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => go("/")}>{t("nav.home")}</button>
              <button onClick={() => go("/cutter")}>{t("nav.cutter")}</button>
              <button onClick={() => go("/design")}>{t("nav.design")}</button>
              <button onClick={() => go("/gallery")}>{t("nav.gallery")}</button>
              {orderMenuVisible && <button onClick={() => go("/order")}>{t("nav.order")}</button>}
              {PRICING_OPEN && <button onClick={() => go("/pricing")}>{t("nav.pricing")}</button>}
              <div className="mobile-menu-divider" />
              {/* 고객지원 (2026-10-16 개편) */}
              <button onClick={() => go("/guide")}>{t("nav.guide")}</button>
              <button onClick={() => go("/printing-faq")}>{t("nav.faq")}</button>
              <div className="mobile-menu-divider" />
              <button onClick={() => { setShowContact(true); setMobileMenuOpen(false); }}>{t("nav.contact")}</button>
              <div className="mobile-menu-divider" />
              {user ? (
                <>
                  <button onClick={() => go("/mypage")}>{t("nav.mypage")}</button>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }}>{t("auth.logout")}</button>
                </>
              ) : (
                <>
                  <button onClick={() => go("/login")}>{t("auth.login")}</button>
                </>
              )}
              {/* 폰에선 상단바의 언어(🌐) 버튼이 숨어서 언어를 바꿀 길이 없었다 → 메뉴 맨 아래에 둔다 (2026-10-19 김현비) */}
              <div className="mobile-menu-divider" />
              <div className="mobile-lang">
                {[["ko", "한국어"], ["en", "English"], ["ja", "日本語"], ["zh", "简体中文"], ["es", "Español"]].map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    className={(i18n.resolvedLanguage || i18n.language || "").startsWith(code) ? "on" : ""}
                    onClick={() => { i18n.changeLanguage(code); setMobileMenuOpen(false); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        </>
      )}

      <Switch>
        {/* ===== 관리자 (admin만 접근) ===== */}
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin" render={() =>
          user?.role === "admin" ? <AdminLayout /> : <HomePage />
        } />

        {/* ===== 일반 ===== */}
        <Route exact path="/" component={HomePage} />
        <Route path="/cutter" component={CutterPage} />
        <Route path="/design" component={DesignPage} />
        <Route path="/gallery/write" component={GalleryWrite} />
        <Route exact path="/gallery" component={GalleryPage} />
        <Route path="/gallery/:id" component={GalleryDetail} />
        <Route path="/login" component={LoginPage} />
        <Route path="/register" component={RegisterPage} />
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/update-password" component={UpdatePasswordPage} />
        <Route exact path="/mypage" component={MyPage} />
        <Route
          path="/pricing"
          render={(props) => (PRICING_OPEN ? <PricingPage {...props} /> : <Redirect to="/" />)}
        />
        <Route path="/payment/success" component={PaymentSuccess} />
        <Route path="/payment/cancel" component={PaymentCancel} />
        <Route path="/payment/toss/billing-success" component={TossBillingSuccess} />
        <Route path="/payment/toss/billing-fail" component={TossBillingFail} />
        <Route path="/payment/toss/success" component={TossPaymentSuccess} />
        <Route path="/payment/toss/fail" component={TossPaymentFail} />
        <Route path="/payment/kakao/success" component={KakaoPaymentSuccess} />
        <Route path="/payment/kakao/fail" component={KakaoPaymentFail} />
        <Route path="/payment/kakao/billing-success" component={KakaoBillingSuccess} />
        <Route path="/payment/kakao/billing-fail" component={KakaoBillingFail} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/service-info" component={ServiceInfoPage} />
        <Route path="/guide" component={UserGuidePage} />
        <Route exact path="/order" component={OrderPage} />
        {/* 결제 페이지는 닫아 둔다 (2026-09-08 김현비) — 결제·주문은 네이버 스마트스토어에서 한다.
            코드는 그대로 두고 길만 막는다. 다시 열려면 PAYMENT_OPEN 을 true 로. */}
        <Route
          path="/order/payment"
          render={(props) => (PAYMENT_OPEN ? <PaymentPage {...props} /> : <Redirect to="/order" />)}
        />
        <Route path="/order/lookup" component={OrderLookup} />
        <Route path="/embed/order" component={EmbedOrderPage} />
        <Route path="/printing-faq" component={PrintingFAQ} />
      </Switch>
      {!isAdminRoute && !isEmbedRoute && location.pathname !== "/design" && <Footer />}

      {!isAdminRoute && !isEmbedRoute && location.pathname !== "/design" && location.pathname !== "/cutter" && (
        <button className="floating-contact-btn" onClick={openContact} title="상담하기" aria-label="상담하기">
          <img src="/happytalk-consult.svg" width="42" height="42" alt="" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}
